/**
 * Token Manager - Secure token storage and management
 * 
 * SECURITY CONTEXT:
 * - Tokens stored in localStorage are vulnerable to XSS attacks
 * - This module provides two modes:
 *   1. httpOnly cookies (Sanctum stateful) - RECOMMENDED, requires backend config
 *   2. localStorage bearer token - Fallback
 *
 * Client session lifetime is controlled by sessionManager's persisted,
 * environment-configured API inactivity deadline. The backend remains authoritative and
 * may reject a token at any time with 401.
 * 
 * Reference: Security Audit #15 - Access token in LocalStorage
 */

const USE_HTTPONLY_COOKIES = process.env.VUE_APP_USE_HTTPONLY_COOKIES === 'true';

// Track token metadata for anomaly detection
const tokenMetadata = {
  issuedAt: null,
  lastUsedAt: null,
  useCount: 0,
  fingerprint: null, // User agent + IP hash for anomaly detection
};

/**
 * Get current token fingerprint (basic anomaly detection)
 * In production, combine with backend IP validation
 */
function getTokenFingerprint() {
  const userAgent = navigator.userAgent;
  // Simple hash of user agent - in production, combine with server-side IP validation
  return btoa(userAgent).substring(0, 20);
}

/**
 * Store token with metadata (localStorage fallback mode).
 */
function storeToken(token) {
  if (USE_HTTPONLY_COOKIES) {
    // With httpOnly cookies, frontend doesn't store the token
    return;
  }

  const now = Date.now();
  
  tokenMetadata.issuedAt = now;
  tokenMetadata.lastUsedAt = now;
  tokenMetadata.fingerprint = getTokenFingerprint();
  tokenMetadata.useCount = 0;
  
  try {
    localStorage.setItem('access_token', token);
    localStorage.removeItem('token_issued_at');
    localStorage.removeItem('token_expires_at');

  } catch (error) {
    console.error('[Security] Failed to store token:', error);
  }
}

/**
 * Retrieve current token
 */
function getToken() {
  if (USE_HTTPONLY_COOKIES) {
    // With httpOnly cookies, token is automatically sent with requests
    return null;
  }
  
  const token = localStorage.getItem('access_token');
  
  if (token) {
    tokenMetadata.lastUsedAt = Date.now();
  }
  
  return token;
}

/**
 * Clear token and metadata
 */
function clearToken() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_issued_at');
    localStorage.removeItem('token_expires_at');
    
    tokenMetadata.issuedAt = null;
    tokenMetadata.lastUsedAt = null;
    tokenMetadata.useCount = 0;
    tokenMetadata.fingerprint = null;
  } catch (error) {
    console.error('[Security] Failed to clear token:', error);
  }
}

/**
 * Check if using httpOnly cookies (stateful authentication)
 */
function isUsingHttpOnlyCookies() {
  return USE_HTTPONLY_COOKIES;
}

/**
 * Log anomaly - used for detecting suspicious token usage patterns
 */
function logAnomaly(reason, details = {}) {
  void reason;
  void details;
  // In production: send to security logging backend
  // Example: POST /api/security/anomalies
  // with reason and details for server-side analysis
}

/**
 * Track bearer-token use for basic anomaly detection.
 */
function recordTokenUse() {
  if (USE_HTTPONLY_COOKIES) return;
  
  tokenMetadata.useCount++;
  
  // Anomaly detection: unusually high token use can indicate abuse.
  if (tokenMetadata.useCount > 100) {
    logAnomaly('Excessive bearer-token use detected', {
      useCount: tokenMetadata.useCount,
    });
  }
}

export default {
  storeToken,
  getToken,
  clearToken,
  isUsingHttpOnlyCookies,
  logAnomaly,
  recordTokenUse,
  getTokenMetadata: () => ({ ...tokenMetadata }),
};
